import { z } from 'zod';

/**
 * Catálogo versionado: produtos, kit fixo, mão de obra, limites e pagamento.
 * Na Central Operacional cada publicação gera uma nova versão; o orçamento salvo
 * guarda o número da versão usada, então um preço alterado depois não muda
 * um orçamento já enviado.
 */

export const ChaveProduto = z.enum([
  'central',
  'bateria',
  'sirene',
  'cabo',
  'aberturaSemFio',
  'presencaComFio',
  'presencaSemFio',
  'controle',
]);
export type ChaveProduto = z.infer<typeof ChaveProduto>;

const Produto = z.object({
  nome: z.string().min(1),
  modelo: z.string().min(1),
  marca: z.string().min(1),
  /** Descrição curta usada no assistente e no memorial. */
  descricao: z.string(),
  unidade: z.enum(['un', 'm']),
  precoCentavos: z.number().int().nonnegative(),
  ativo: z.boolean(),
});
export type Produto = z.infer<typeof Produto>;

export const Catalogo = z.object({
  versao: z.number().int().positive(),
  produtos: z.object({
    central: Produto,
    bateria: Produto,
    sirene: Produto,
    cabo: Produto,
    aberturaSemFio: Produto,
    presencaComFio: Produto,
    presencaSemFio: Produto,
    controle: Produto,
  }),
  /** Itens que entram em todo projeto, com quantidade. */
  kitFixo: z.array(z.object({ produto: ChaveProduto, quantidade: z.number().int().positive() })).min(1),
  maoDeObra: z.object({
    porPontoCentavos: z.number().int().nonnegative(),
    minimoCentavos: z.number().int().nonnegative(),
  }),
  limites: z.object({
    /** semFioMax = zonas sem fio da central: cada porta, janela ou sensor de presença sem fio ocupa uma. */
    presencaComFioMax: z.number().int().positive(),
    semFioMax: z.number().int().positive(),
    controlesMax: z.number().int().nonnegative(),
    areaExternaMaxMetros: z.number().int().positive(),
  }),
  pagamento: z.object({
    pixDescontoPercentual: z.number().int().min(0).max(100),
    parcelasMax: z.number().int().positive(),
    /** Parcelamento no cartão sem juros: o valor da parcela é o total dividido pelo número de parcelas. */
    parcelasSemJuros: z.boolean(),
  }),
  garantia: z.object({
    equipamentosMeses: z.number().int().positive(),
    instalacaoDias: z.number().int().positive(),
  }),
});
export type Catalogo = z.infer<typeof Catalogo>;

/**
 * Catálogo v1 — valores aprovados em 14/10/2026.
 * Na implantação este objeto vira a primeira linha de `catalogo_versoes` no banco;
 * depois disso os valores passam a ser editados só pela Central.
 */
export const CATALOGO_V1: Catalogo = Catalogo.parse({
  versao: 1,
  produtos: {
    central: {
      nome: 'Central de alarme JFL SmartCloud 32',
      modelo: 'SmartCloud 32',
      marca: 'JFL',
      descricao: 'Wi-Fi integrado e nuvem gratuita',
      unidade: 'un',
      precoCentavos: 43900,
      ativo: true,
    },
    bateria: {
      nome: 'Bateria 12V 7Ah',
      modelo: '12V 7Ah',
      marca: 'Nobreak',
      descricao: 'Mais de 20 horas de funcionamento sem energia',
      unidade: 'un',
      precoCentavos: 11529,
      ativo: true,
    },
    sirene: {
      nome: 'Sirene Intelbras SIR 1000 BC 105 dB',
      modelo: 'SIR 1000 BC',
      marca: 'Intelbras',
      descricao: '105 dB',
      unidade: 'un',
      precoCentavos: 4590,
      ativo: true,
    },
    cabo: {
      nome: 'Cabo 4 vias',
      modelo: '04x50 multicores',
      marca: 'Genérico',
      descricao: 'Cabeamento das sirenes e sensores com fio',
      unidade: 'm',
      precoCentavos: 197,
      ativo: true,
    },
    aberturaSemFio: {
      nome: 'Sensor de abertura sem fio SH-FIT',
      modelo: 'Fit Hopping Code 433 MHz',
      marca: 'JFL',
      descricao: 'Hopping Code, anticlonagem · portas e janelas',
      unidade: 'un',
      precoCentavos: 7325,
      ativo: true,
    },
    presencaComFio: {
      nome: 'Sensor de presença com fio Hikvision DS-PDP18-EG2',
      modelo: 'DS-PDP18-EG2',
      marca: 'Hikvision',
      descricao: 'Alta precisão · imune a pets de até 20 kg',
      unidade: 'un',
      precoCentavos: 8325,
      ativo: true,
    },
    presencaSemFio: {
      nome: 'Sensor de presença sem fio JFL IRPET 530 SF',
      modelo: 'IRPET 530 SF',
      marca: 'JFL',
      descricao: 'Pet free',
      unidade: 'un',
      precoCentavos: 12900,
      ativo: true,
    },
    controle: {
      nome: 'Controle remoto',
      modelo: 'Código variável',
      marca: 'JFL',
      descricao: 'Código variável, anticlonagem',
      unidade: 'un',
      precoCentavos: 3990,
      ativo: true,
    },
  },
  kitFixo: [
    { produto: 'central', quantidade: 1 },
    { produto: 'bateria', quantidade: 1 },
    { produto: 'sirene', quantidade: 2 },
    { produto: 'cabo', quantidade: 15 },
  ],
  maoDeObra: { porPontoCentavos: 2500, minimoCentavos: 25000 },
  limites: {
    presencaComFioMax: 6,
    semFioMax: 32,
    controlesMax: 10,
    areaExternaMaxMetros: 30,
  },
  pagamento: { pixDescontoPercentual: 6, parcelasMax: 10, parcelasSemJuros: true },
  garantia: { equipamentosMeses: 12, instalacaoDias: 90 },
});
